/* DCG v2-10-4 · 13 OCT 2026 · the row's cards are the DCG product card: tabs and the rail only (item 3) */
/* =========================================================
   Desert City Games – DCG Top Singles
   File: assets/dcg-top-singles.js

   Features:
   - Tab switching (click + keyboard arrows)
   - A tab's cards are drawn when it is first opened, and
     handed to assets/dcg-product-card.js to be made live
   - Rail arrows: a screen of cards at a press; dimmed at an end
   - The DCG cart bridge fallback (window.DCG.cart.addItem),
     which assets/dcg-events.js also uses

   DCG v2-10-4 (item 3; Cole, 13 OCT 2026). Each product in the row
   is now the DCG product card (snippets/dcg-product-card.liquid),
   the same card as on a collection page. What this file did for
   the row's old card is gone with that card:
   - the variant select -> hidden input, price and foil sync
     (the DCG card script does that for its own card);
   - the add-to-cart form handler and its notice (the DCG card's
     button adds through assets/dcg-cart-bridge.js, with the
     enforcer's checks, like every other DCG card);
   - the looping rail: it copied the first and last cards to
     either end, and a copy of a DCG card has none of its controls
     working (and repeats its ids);
   - the focus / dim effect on the middle card.
========================================================= */

(() => {
  "use strict";

  /* ---------------------------------------------------
     DCG Cart Bridge
     Piggybacks theme: /cart/add.js + adjustCartDropDown()
  --------------------------------------------------- */
  (function ensureDcgCartBridge() {
    window.DCG = window.DCG || {};
    window.DCG.cart = window.DCG.cart || {};
    if (typeof window.DCG.cart.addItem === "function") return;

    window.DCG.cart.addItem = async function ({ id, quantity = 1, properties } = {}) {
      if (!id) throw new Error("Missing variant id");

      // ── Pre-flight enforcement (mirrors dcg-cart-bridge.js) ────────────────
      // Ensures limit:N tags are honored even on pages where the full cart
      // bridge script isn't loaded (e.g. the homepage Top Singles rail).
      // window.DCG.enforcer is defined globally by dcg-cart-enforcer.js in
      // <head>. Without this, the fallback add path skipped all limit checks,
      // so enforcement depended on which script happened to load first.
      if (window.DCG && typeof window.DCG.enforcer === "object" && window.DCG.enforcer) {
        let _enforcerBlocked = false;
        try {
          const check = await window.DCG.enforcer.preflight(id, quantity);
          if (check.blocked) {
            _enforcerBlocked = true;
            window.DCG.enforcer.showToast(check.message || "Purchase limit reached.");
            throw new Error(check.message || "Purchase limit reached.");
          }
          if (check.message) {
            window.DCG.enforcer.showToast(check.message);
          }
          quantity = check.qty;
        } catch (enforceErr) {
          if (_enforcerBlocked) throw enforceErr;
          console.warn("[DCG] enforcer preflight error — allowing add:", enforceErr);
        }
      }

      const payload = { id: Number(id), quantity: Number(quantity || 1) };
      if (properties) payload.properties = properties;

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data?.description || data?.message || "Add to cart failed");
        } catch (_) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Add to cart failed");
        }
      }

      const data = await res.json();

      if (typeof window.adjustCartDropDown === "function") {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }

      // Always dispatch cart:updated so dcg-cart-enforcer.js can run its
      // post-add drawer audit regardless of which refresh path was taken.
      document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));

      return data;
    };
  })();


  /* ---------------------------------------------------
     Utilities
  --------------------------------------------------- */
  const ROOT_SEL = ".dcg-pb";

  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function reducedMotion() {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (_) { return false; }
  }


  /* ---------------------------------------------------
     The cards (DCG v2-10-4)
     Each product is the DCG product card, drawn by
     snippets/dcg-product-card.liquid and made live by
     assets/dcg-product-card.js. That script builds the
     cards it finds when the page has been read. The cards
     of a tab that has not been opened yet are in a
     <template> and are not in the page then: when a tab is
     opened its cards are handed to the same script.
  --------------------------------------------------- */
  function initCards(panel) {
    try {
      if (typeof window.dcgInitProductCards === "function") window.dcgInitProductCards(panel);
      // the card script's own "cards were drawn" event: it builds any card not
      // built yet and puts the hearts of wishlisted products back
      document.dispatchEvent(new CustomEvent("dcg:collection:rendered"));
    } catch (err) {
      console.warn("[DCG top singles] card init failed:", err);
    }
  }


  /* ---------------------------------------------------
     Tabs
  --------------------------------------------------- */
  function hydratePanel(panel) {
    const tpl = panel.querySelector(":scope > template[data-dcg-panel-template]");
    if (!tpl) return false;
    panel.appendChild(tpl.content);
    tpl.remove();
    initCards(panel);
    return true;
  }

  function setActiveTab(root, tabTargetId) {
    qsa(".dcg-pb__tab", root).forEach((btn) => {
      const active = btn.dataset.tabTarget === tabTargetId;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
      btn.tabIndex = active ? 0 : -1;
    });

    qsa(".dcg-pb__panel", root).forEach((panel) => {
      const active = panel.dataset.panel === tabTargetId;
      panel.classList.toggle("is-active", active);
      if (active) {
        panel.removeAttribute("hidden");
        hydratePanel(panel);
        bindRailEnds(panel);
      } else {
        panel.setAttribute("hidden", "");
      }
    });
  }

  function bindTabKeyboard(root) {
    const tablist = qs(".dcg-pb__tabs", root);
    if (!tablist || tablist.__dcgKeyBound) return;
    tablist.__dcgKeyBound = true;

    tablist.addEventListener("keydown", (e) => {
      const tabs = qsa(".dcg-pb__tab", tablist);
      if (!tabs.length) return;

      const current = tabs.findIndex((t) => t.classList.contains("is-active"));
      let next = -1;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        next = (current + 1) % tabs.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        next = (current - 1 + tabs.length) % tabs.length;
      } else if (e.key === "Home") {
        next = 0;
      } else if (e.key === "End") {
        next = tabs.length - 1;
      }

      if (next >= 0) {
        e.preventDefault();
        tabs[next].focus();
        const target = tabs[next].dataset.tabTarget;
        if (target) setActiveTab(root, target);
      }
    });
  }


  /* ---------------------------------------------------
     Rail scrolling
     An arrow moves the rail by the cards that are wholly in
     view (two on a phone), and at least by one. The rail
     does not loop: at either end the arrow that leads
     nowhere is dimmed (.is-end).
  --------------------------------------------------- */
  function railMetrics(rail) {
    const card = qs(".dcg-pb__item", rail);
    const cardW = card ? card.getBoundingClientRect().width : 240;
    const gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 16;
    return { step: cardW + gap, cardW, gap };
  }

  function scrollRail(panelEl, dir) {
    const rail = qs(".dcg-pb__rail", panelEl);
    if (!rail) return;
    const m = railMetrics(rail);
    const inView = Math.max(1, Math.floor((rail.clientWidth + m.gap) / m.step));
    rail.scrollBy({ left: dir * inView * m.step, behavior: reducedMotion() ? "auto" : "smooth" });
  }

  function updateRailEnds(panel) {
    const rail = qs(".dcg-pb__rail", panel);
    if (!rail) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const prev = qs(".dcg-pb__nav--prev", panel);
    const next = qs(".dcg-pb__nav--next", panel);
    if (prev) prev.classList.toggle("is-end", rail.scrollLeft <= 2);
    if (next) next.classList.toggle("is-end", max <= 2 || rail.scrollLeft >= max - 2);
  }

  function bindRailEnds(panel) {
    const rail = qs(".dcg-pb__rail", panel);
    if (!rail) return;
    if (!rail.__dcgEndsBound) {
      rail.__dcgEndsBound = true;
      let ticking = false;
      rail.addEventListener("scroll", () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; updateRailEnds(panel); });
      }, { passive: true });
    }
    requestAnimationFrame(() => updateRailEnds(panel));
  }


  /* ---------------------------------------------------
     Per-section init
  --------------------------------------------------- */
  function initOne(root) {
    // Tab click
    const tablist = qs(".dcg-pb__tabs", root);
    if (tablist && !tablist.__dcgBound) {
      tablist.__dcgBound = true;
      tablist.addEventListener("click", (e) => {
        const btn = e.target.closest(".dcg-pb__tab");
        if (!btn) return;
        const target = btn.dataset.tabTarget;
        if (target) setActiveTab(root, target);
      });
    }

    // Tab keyboard
    bindTabKeyboard(root);

    // Set initial tabindex on tabs
    qsa(".dcg-pb__tab", root).forEach((btn) => {
      btn.tabIndex = btn.classList.contains("is-active") ? 0 : -1;
    });

    // Rail arrow clicks
    if (!root.__dcgNavBound) {
      root.__dcgNavBound = true;
      root.addEventListener("click", (e) => {
        const prev = e.target.closest(".dcg-pb__nav--prev");
        const next = e.target.closest(".dcg-pb__nav--next");
        if (!prev && !next) return;

        const panel = e.target.closest(".dcg-pb__panel");
        if (!panel) return;
        scrollRail(panel, prev ? -1 : 1);
      });
      window.addEventListener("resize", () => {
        qsa(".dcg-pb__panel.is-active", root).forEach(updateRailEnds);
      }, { passive: true });
    }

    // The open tab: its cards are in the page already and are built by
    // assets/dcg-product-card.js itself (in the theme editor, by its own
    // shopify:section:load handler)
    qsa(".dcg-pb__panel.is-active", root).forEach(bindRailEnds);
  }

  function initAll() {
    qsa(ROOT_SEL).forEach(initOne);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initAll);
  else initAll();
  document.addEventListener("shopify:section:load", initAll);
})();
